import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Why would I DRIP in my Roth but not in my taxable account?',
    a: 'Roth IRA has no tax friction — every reinvested dividend compounds tax-free forever. Taxable accounts pay tax on every dividend whether you reinvest or take cash, AND each DRIP buy creates a new tax lot that complicates future selling. So in Roth: DRIP everything. In taxable: take cash so you have flexibility and avoid lot-tracking headaches.',
  },
  {
    q: 'Doesn\'t DRIP in a taxable account still beat manual reinvestment?',
    a: 'Behaviourally yes (it removes the friction of deciding what to do with the cash), but mechanically it can hurt. Each DRIP buy gets a unique cost basis at that day\'s price, fragmenting your position into dozens of micro-lots. When you eventually sell, you\'re juggling lot-by-lot tax decisions. Many investors prefer cash dividends in taxable + a quarterly manual reinvestment into whatever\'s most attractive.',
  },
  {
    q: 'What about 401(k) and traditional IRA?',
    a: 'Same as Roth in terms of DRIP behaviour — no immediate tax friction, so DRIP everything. The difference is tax treatment at withdrawal (Roth = tax-free; traditional = ordinary income), but that doesn\'t affect the during-accumulation DRIP decision.',
  },
  {
    q: 'Does this rule apply to ETFs the same way it applies to individual stocks?',
    a: 'Mostly yes, with one nuance. ETF dividends often include a small return-of-capital component that doesn\'t generate immediate taxable income; for those specific ETFs, DRIP in taxable is less painful. But for the typical SCHD/VYM/JEPI dividend ETF, the rule still holds: DRIP in tax-advantaged, cash in taxable.',
  },
  {
    q: 'How does DiviDrip handle lot tracking?',
    a: 'The Tax Lots tool (under My Portfolio → Tool Box) tracks every lot — including DRIP lots — with its own cost basis and holding-period status. So if you DO choose to DRIP in taxable, you have a clean view of every lot when it comes time to sell.',
  },
];

export default function LearnWhyDripRoth() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Why I DRIP in My Roth But Not in My Taxable — Account-Specific Reinvestment | DiviDrip"
      description="Why dividend reinvestment math changes dramatically based on which account holds the position. DRIP in tax-advantaged accounts, take cash in taxable — here's why."
      canonical="https://twylightcrow.com/learn/why-drip-roth"
      guidePath="/learn/why-drip-roth"
      sectionLabel="Tax Strategy"
      sectionAnchor="tax"
      kicker="Learn · Tax strategy"
      h1="Why I DRIP in My Roth But Not in My Taxable"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/drip-vs-cash-dividends', label: 'DRIP vs cash dividends — which to choose?' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
        { to: '/learn/four-phases', label: 'The 4 phases of a dividend investor\'s life' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Most dividend-investing guides treat DRIP as a binary on/off switch. It isn't.
        The right answer changes based on which account holds the position — and the
        difference between the right and wrong choice compounds into thousands of
        dollars over a 30-year horizon.
      </p>
      <p>
        Here's the rule I follow, the math behind it, and the one case where it
        doesn't apply.
      </p>

      <h2>The rule (one sentence)</h2>
      <p className="text-lg font-semibold">
        DRIP everything in tax-advantaged accounts (Roth IRA, traditional IRA, 401(k),
        HSA). Take cash dividends in taxable brokerage accounts.
      </p>

      <h2>Why DRIP is great in a Roth IRA</h2>
      <p>
        In a Roth, every dividend that gets reinvested:
      </p>
      <ul>
        <li>Pays zero tax in the year received</li>
        <li>Buys more shares automatically (no friction, no decision)</li>
        <li>Lets those new shares pay future dividends — also tax-free</li>
        <li>Compounds for decades with zero tax drag</li>
      </ul>
      <p>
        There is no cost. DRIP is purely additive. The only "cost" is opportunity —
        you give up the chance to redeploy that cash into a different stock you might
        prefer. But for the broad ETFs and Kings most people hold in their Roth, that
        opportunity cost is small.
      </p>

      <h2>Why DRIP is painful in a taxable account</h2>
      <p>
        In taxable, every dividend is taxable in the year received regardless of
        whether you DRIP it or take cash. So DRIP doesn't save you any tax. What it
        DOES do is create a new tax lot — a separate cost-basis record — for every
        reinvestment.
      </p>
      <p>
        A SCHD position held for 10 years and DRIP'ed quarterly will have 40+ tax
        lots, each with its own purchase price and holding period. When you eventually
        sell, you're no longer selling a single position — you're choosing which of
        40 lots to sell, weighing short-term vs long-term gain rates, and trying to
        minimise the tax bill on what should be a simple transaction.
      </p>
      <p>
        Worse: if you DRIP and then realise within 30 days that you want to sell at a
        loss for tax-loss harvesting purposes, the DRIP purchase triggers the wash-sale
        rule and disallows the loss. The IRS doesn't care that the DRIP was automatic.
      </p>

      <h2>The mechanically-better taxable workflow</h2>
      <ol>
        <li>Turn off DRIP on every holding in your taxable account.</li>
        <li>Let dividends sit as cash inside the account.</li>
        <li>Once a quarter (or when cash builds to a meaningful amount), make ONE buy across all holdings — pick the most attractively-priced one or the one most underweight in your target allocation.</li>
        <li>This gives you one tax lot per quarter per holding instead of fifty.</li>
      </ol>
      <p>
        It takes maybe 10 minutes a quarter. The lot-count reduction alone is worth it.
        The strategic flexibility (deploying into whatever's on sale that quarter) is
        a bonus.
      </p>

      <h2>The behavioural risk</h2>
      <p>
        DRIP exists for a real reason — many investors take cash dividends and then
        let the cash sit uninvested for months while life happens. Manual reinvestment
        only beats DRIP if you actually DO it. If you know yourself well enough to
        admit you won't, leave DRIP on even in taxable — the suboptimal-but-deployed
        outcome beats the theoretically-optimal-but-uninvested one.
      </p>

      <h2>The exception — ETFs with return-of-capital distributions</h2>
      <p>
        Some specific ETFs (certain covered-call funds, some MLP funds, infrastructure
        funds) distribute a meaningful portion of return-of-capital, which isn't
        taxed in the year received — it lowers your cost basis instead. For these
        funds, DRIP in taxable creates fewer immediate-tax-bill problems. But it still
        creates the lot-fragmentation problem. So the rule mostly holds.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard?view=portfolio">DiviDrip</Link>, head to{' '}
        <strong>My Portfolio → Tool Box → Tax Lots</strong>, and look at how many lots
        you currently have for each of your taxable-account positions. If the number
        is over 20 per position, switching to cash dividends + quarterly manual buys
        will dramatically simplify your future tax life.
      </p>
      <p className="text-xs text-muted-foreground italic mt-6">
        Not tax advice. Consult a tax professional for your specific situation.
      </p>
    </LearnPage>
  );
}
